import { Component } from '@theme/component'
import { isPointWithinElement } from '@theme/maths'

const CURSOR_STATE_VALUES = ['close']

const CURSOR_STATE_SELECTOR = CURSOR_STATE_VALUES.map(
  (state) => `[data-cursor="${state}"]`
).join(', ')

const CustomCursorConfig = {
  coarsePointerQuery: '(hover: none) and (pointer: coarse)',
  fadeDuration: 0.2,
  interactiveSelector: `${CURSOR_STATE_SELECTOR}, a, button, [role="button"], summary, label, input, textarea, [contenteditable="true"]`,
  pointerSelector: 'a, button, [role="button"], summary, label',
  stateAttribute: 'data-cursor',
  stateValues: CURSOR_STATE_VALUES
}

class CustomCursorElement extends Component {
  requiredRefs = ['dot', 'hud']

  currentType = null
  dotTo = null
  fadeTween = null
  hasPointerPosition = false
  hudTo = null
  isActive = false
  lastTarget = null
  pointerX = 0
  pointerY = 0

  onPointerMove = (event) => {
    const { clientX, clientY } = event
    this.pointerX = clientX
    this.pointerY = clientY

    if (!this.hasPointerPosition) this.revealAt(clientX, clientY)

    this.followPointer(clientX, clientY)
    this.lastTarget = event.target
    this.resolveType(event.target)
  }

  onPointerOut = () => {
    this.hasPointerPosition = false
    this.restoreNativeCursor()
    this.fadeTween = window.gsap.to([this.refs.dot, this.refs.hud], {
      autoAlpha: 0,
      duration: CustomCursorConfig.fadeDuration
    })
  }

  onDialogToggle = (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return
    if (!this.isActive) return
    this.promoteToTopLayer()
  }

  connectedCallback() {
    super.connectedCallback()

    if (matchMedia(CustomCursorConfig.coarsePointerQuery).matches) return
    if (!window.gsap) return

    this.setupFollow()
    this.activate()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.deactivate()
  }

  setupFollow() {
    const { dot, hud } = this.refs

    window.gsap.set([dot, hud], { autoAlpha: 0 })
    window.gsap.set(dot, { xPercent: -50, yPercent: -50 })

    this.dotTo = this.createFollower(dot)
    this.hudTo = this.createFollower(hud)
  }

  createFollower(element) {
    return {
      x: window.gsap.quickSetter(element, 'x', 'px'),
      y: window.gsap.quickSetter(element, 'y', 'px')
    }
  }

  activate() {
    this.isActive = true
    this.promoteToTopLayer()
    window.addEventListener('mousemove', this.onPointerMove, { passive: true })
    document.documentElement.addEventListener('mouseleave', this.onPointerOut)
    document.addEventListener('toggle', this.onDialogToggle, true)
  }

  deactivate() {
    if (!this.isActive) return
    this.isActive = false
    this.hasPointerPosition = false
    this.killFade()
    this.restoreNativeCursor()
    this.dismissFromTopLayer()
    window.removeEventListener('mousemove', this.onPointerMove)
    document.documentElement.removeEventListener(
      'mouseleave',
      this.onPointerOut
    )
    document.removeEventListener('toggle', this.onDialogToggle, true)
  }

  promoteToTopLayer() {
    for (const element of [this.refs.dot, this.refs.hud]) {
      if (element.matches(':popover-open')) element.hidePopover()
      element.showPopover()
    }
  }

  dismissFromTopLayer() {
    for (const element of [this.refs.dot, this.refs.hud]) {
      if (element.matches(':popover-open')) element.hidePopover()
    }
  }

  revealAt(x, y) {
    this.hasPointerPosition = true
    this.killFade()
    window.gsap.set([this.refs.dot, this.refs.hud], { autoAlpha: 1, x, y })
    document.documentElement.dataset.customCursor = 'active'
  }

  restoreNativeCursor() {
    delete document.documentElement.dataset.customCursor
  }

  killFade() {
    this.fadeTween?.kill()
    this.fadeTween = null
  }

  followPointer(x, y) {
    this.dotTo.x(x)
    this.dotTo.y(y)
    this.hudTo.x(x)
    this.hudTo.y(y)
  }

  resolveType(target) {
    if (!(target instanceof Element)) {
      this.applyType(null)
      return
    }
    if (this.resolveDialogBackdrop(target)) return

    const interactive = target.closest(CustomCursorConfig.interactiveSelector)
    if (interactive) {
      this.applyInteractive(interactive)
      return
    }

    this.applyType(null)
  }

  resolveDialogBackdrop(target) {
    const dialog =
      target instanceof HTMLDialogElement ? target : target.closest('dialog')
    if (!dialog?.open) return false
    if (!dialog.hasAttribute('data-overlay')) return false

    const panel = dialog.querySelector(
      '.popover-card, .drawer-panel, [data-drawer-panel]'
    )
    if (panel && isPointWithinElement(this.pointerX, this.pointerY, panel)) {
      return false
    }

    this.applyType('close')
    return true
  }

  applyInteractive(element) {
    const stated = element.getAttribute(CustomCursorConfig.stateAttribute)
    if (CustomCursorConfig.stateValues.includes(stated)) {
      this.applyType(stated)
      return
    }
    if (element.matches(CustomCursorConfig.pointerSelector)) {
      this.applyType('pointer')
      return
    }

    this.applyType('input')
  }

  applyType(type) {
    if (type === this.currentType) return
    this.currentType = type
    this.writeType(type)
  }

  writeType(type) {
    if (!type) {
      this.removeAttribute('data-type')
      return
    }
    this.dataset.type = type
  }
}

if (!customElements.get('custom-cursor')) {
  customElements.define('custom-cursor', CustomCursorElement)
}
